import { useEffect, useState, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { fmtDate, todayIso } from "@/lib/entries";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import StatusBadge from "@/components/StatusBadge";
import EntryDialog from "@/components/EntryDialog";
import CloseFollowupDialog from "@/components/CloseFollowupDialog";
import CloseReasonsSettingsDialog from "@/components/CloseReasonsSettingsDialog";
import PatientHistoryDialog from "@/components/PatientHistoryDialog";
import { PhoneCall, Search, X, MessageCircle, FlaskConical, XCircle, Settings, Plus } from "lucide-react";

export default function FollowUpsSheet() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState("pending");
  const [editEntry, setEditEntry] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [newOpen, setNewOpen] = useState(false);
  const [newName, setNewName] = useState("");
  const [savingNew, setSavingNew] = useState(false);
  const [closeEntry, setCloseEntry] = useState(null);
  const [closeOpen, setCloseOpen] = useState(false);
  const [reasonsOpen, setReasonsOpen] = useState(false);
  const [historyName, setHistoryName] = useState(null);
  const today = todayIso();

  const load = useCallback(async () => {
    try { const { data } = await api.get("/sheets/followups"); setRows(data); }
    catch (e) { toast.error(formatApiError(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const counts = useMemo(() => {
    const hasComms = (e) => (e.communications || []).length > 0 || (e.followup_history || []).length > 0;
    return {
      pending:      rows.filter((e) => !e.is_complete && !hasComms(e)).length,
      in_convo:     rows.filter((e) => !e.is_complete && hasComms(e)).length,
      completed:    rows.filter((e) =>  e.is_complete).length,
      all:          rows.length,
    };
  }, [rows]);

  const filtered = useMemo(() => {
    const hasComms = (e) => (e.communications || []).length > 0 || (e.followup_history || []).length > 0;
    const s = q.trim().toLowerCase();
    const list = rows.filter((e) => {
      if (tab === "pending"    && (e.is_complete || hasComms(e))) return false;
      if (tab === "in_convo"   && (e.is_complete || !hasComms(e))) return false;
      if (tab === "completed"  && !e.is_complete) return false;
      if (s && !e.patient_name.toLowerCase().includes(s)) return false;
      return true;
    });
    if (tab === "completed") {
      // Most recent completions on top.
      list.sort((a, b) => {
        const av = a.closure?.closed_at || a.completed_at || "";
        const bv = b.closure?.closed_at || b.completed_at || "";
        return bv.localeCompare(av);
      });
    } else if (tab === "pending") {
      // Oldest scheduled follow-up at the top so overdue rows surface first.
      list.sort((a, b) => {
        const av = a.scheduled_date || a.followup_date || "";
        const bv = b.scheduled_date || b.followup_date || "";
        if (av !== bv) return av.localeCompare(bv);
        return (a.created_at || "").localeCompare(b.created_at || "");
      });
    } else {
      // In Conversation / All → latest scheduled follow-up first.
      list.sort((a, b) => {
        const av = a.scheduled_date || a.followup_date || "";
        const bv = b.scheduled_date || b.followup_date || "";
        if (av !== bv) return bv.localeCompare(av);
        return (b.created_at || "").localeCompare(a.created_at || "");
      });
    }
    return list;
  }, [rows, q, tab]);

  // For the Completed tab: when `next_date` wasn't persisted on the closed row
  // (legacy rows closed before that field was recorded), look up the sibling
  // follow-up entry created for the same patient right after the closure and
  // use its scheduled_date. This surfaces the next follow-up date the user
  // picked at close time without any data migration.
  const scheduledByPatient = useMemo(() => {
    const map = new Map();
    for (const r of rows) {
      const sched = r.scheduled_date || r.followup_date;
      if (!sched) continue;
      const key = r.patient_name;
      if (!map.has(key)) map.set(key, []);
      map.get(key).push({ id: r.id, sched, createdAt: r.created_at || "" });
    }
    for (const arr of map.values()) arr.sort((a, b) => a.sched.localeCompare(b.sched));
    return map;
  }, [rows]);

  const startEdit = (e) => { setEditEntry(e); setEditOpen(true); };
  const startClose = (e) => { setCloseEntry(e); setCloseOpen(true); };

  const openNewFollowup = () => { setNewName(""); setNewOpen(true); };
  const submitNewFollowup = async (ev) => {
    ev.preventDefault();
    const name = newName.trim();
    if (!name) { toast.error("Please enter the patient's name"); return; }
    setSavingNew(true);
    try {
      const t = todayIso();
      await api.post("/entries", {
        patient_name: name,
        entry_date: t,
        status: "ongoing_treatment",
        scheduled_date: t,
        followup_date: t,
        notes: "",
        // Tag as originating from the Follow-up sheet so it stays hidden from
        // Daily Log and shows up under Pending on the Follow-ups sheet only.
        source: "followup_sheet",
      });
      toast.success(`${name} added to Pending`);
      setNewOpen(false);
      setNewName("");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingNew(false);
    }
  };

  return (
    <div className="px-6 lg:px-10 pt-4 lg:pt-6 pb-3 max-w-[1400px] mx-auto">
      <div className="flex items-center gap-3">
        <div className="h-9 w-9 rounded-lg bg-sky-50 text-sky-700 flex items-center justify-center">
          <PhoneCall size={18} />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide text-slate-400 leading-none">Ongoing treatments and cancellations</p>
          <h1 className="font-display text-2xl font-bold text-slate-900 leading-tight">Follow-ups</h1>
        </div>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" variant="outline"
                  onClick={() => setReasonsOpen(true)}
                  data-testid="manage-close-reasons-button">
            <Settings size={14} className="mr-1.5" /> Close reasons
          </Button>
          <Button className="bg-sky-500 hover:bg-sky-600 text-white h-9 px-4"
                  onClick={openNewFollowup}
                  data-testid="new-followup-button">
            <Plus size={16} className="mr-1.5" /> New follow-up
          </Button>
        </div>
      </div>

      <Card className="mt-3 border-slate-200 p-3 flex flex-col sm:flex-row gap-3 sm:items-center">
        <Tabs value={tab} onValueChange={setTab} className="w-full sm:w-auto">
          <TabsList data-testid="followups-tabs">
            <TabsTrigger value="pending"    data-testid="tab-pending">Pending ({counts.pending})</TabsTrigger>
            <TabsTrigger value="in_convo"
                         className="data-[state=active]:bg-emerald-500 data-[state=active]:text-white data-[state=active]:shadow text-emerald-600 font-semibold"
                         data-testid="tab-in-convo">In Conversation ({counts.in_convo})</TabsTrigger>
            <TabsTrigger value="completed"  data-testid="tab-completed">Completed ({counts.completed})</TabsTrigger>
            <TabsTrigger value="all"        data-testid="tab-all">All ({counts.all})</TabsTrigger>
          </TabsList>
        </Tabs>
        <div className="relative flex-1">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input className="pl-9 pr-9" placeholder="Search patient name in this sheet..."
                 value={q} onChange={(e) => setQ(e.target.value)} data-testid="followups-search-input" />
          {q && (
            <button type="button" onClick={() => setQ("")} data-testid="followups-search-clear"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 rounded-md text-slate-400 hover:text-slate-700 hover:bg-slate-100 flex items-center justify-center">
              <X size={14} />
            </button>
          )}
        </div>
      </Card>

      <Card className="mt-3 mb-3 border-slate-200 overflow-hidden [&>div]:max-h-[calc(100vh-190px)] [&>div]:overflow-auto" data-testid="followups-sheet">
        {filtered.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="followups-empty">
            {q ? `No follow-ups matching "${q}".`
               : tab === "pending"    ? "No pending follow-ups. Enjoy the quiet."
               : tab === "in_convo"   ? "No open conversations right now."
               : tab === "completed"  ? "No completed follow-ups yet."
                                      : "No follow-ups on record."}
          </div>
        ) : tab === "completed" ? (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14 text-center">S.No.</TableHead>
                <TableHead className="w-44">Date of Completion</TableHead>
                <TableHead className="min-w-[260px]">Patient</TableHead>
                <TableHead className="w-44">Next Follow-up Date</TableHead>
                <TableHead className="min-w-[220px]">Notes</TableHead>
                <TableHead className="w-32 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => {
                const closedIso = e.closure?.closed_at || e.completed_at;
                const closedDate = closedIso ? closedIso.slice(0, 10) : null;
                const reason = (e.closure?.reason || "").trim();
                const r = reason.toLowerCase();
                let tagLabel = reason;
                let tagClass = "bg-slate-100 text-slate-700 border-slate-200";
                if (r === "next appointment given") {
                  tagLabel = "Next Appointment Given";
                  tagClass = "bg-emerald-50 text-emerald-700 border-emerald-200";
                } else if (r.includes("no further") || r === "no follow-up" || r === "no followup") {
                  tagLabel = "No Follow-up";
                  tagClass = "bg-red-50 text-red-700 border-red-200";
                }
                // Derive next follow-up date: prefer the persisted `next_date`,
                // otherwise reconstruct it from the sibling follow-up row created
                // right after this closure for the same patient.
                let nextDateDisplay = e.next_date;
                if (!nextDateDisplay && r.includes("next follow-up") && closedIso) {
                  const sibs = scheduledByPatient.get(e.patient_name) || [];
                  const closedDayIso = closedIso.slice(0, 10);
                  const candidate = sibs.find(
                    (x) => x.id !== e.id
                      && (x.createdAt || "") > closedIso
                      && x.sched >= closedDayIso,
                  );
                  if (candidate) nextDateDisplay = candidate.sched;
                }
                const notes = (e.closure?.note || "").trim() || e.notes || "";
                return (
                  <TableRow key={e.id} data-testid={`followup-row-${e.id}`}>
                    <TableCell className="text-slate-500 text-sm w-14 text-center" data-testid={`followup-sno-${e.id}`}>{i + 1}</TableCell>
                    <TableCell className="text-slate-900 font-medium whitespace-nowrap w-44"
                               data-testid={`followup-completed-date-${e.id}`}>
                      {closedDate ? fmtDate(closedDate) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="min-w-[260px]">
                      <div className="flex items-center justify-between gap-3">
                        <button type="button" onClick={() => setHistoryName(e.patient_name)}
                                data-testid={`followup-name-${e.id}`}
                                className="font-medium text-slate-900 hover:text-sky-700 hover:underline text-left truncate">
                          {e.patient_name}
                        </button>
                        {tagLabel && (
                          <span className={`shrink-0 inline-flex items-center whitespace-nowrap px-2 py-0.5 rounded border text-[11px] font-medium ${tagClass}`}
                                data-testid={`followup-reason-tag-${e.id}`}>
                            {tagLabel}
                          </span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-slate-700 w-44"
                               data-testid={`followup-next-date-${e.id}`}>
                      {nextDateDisplay ? fmtDate(nextDateDisplay) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="text-slate-600 min-w-[220px] max-w-md truncate">{notes || "—"}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap w-32">
                      <Button size="sm" variant="outline"
                              onClick={() => startEdit(e)}
                              data-testid={`followup-edit-${e.id}`}>
                        Edit
                      </Button>
                      <Link to={`/entries/${e.id}`}>
                        <Button size="sm" variant="outline"
                                data-testid={`open-followup-${e.id}`}>
                          View
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        ) : (
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white shadow-[0_1px_0_0_rgba(226,232,240,1)]">
              <TableRow>
                <TableHead className="w-14">S.No.</TableHead>
                <TableHead>Follow-up Scheduled</TableHead>
                <TableHead>Patient</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filtered.map((e, i) => {
                const scheduled = e.scheduled_date || e.followup_date;
                const done = e.done_date;
                const next = e.next_date;
                const isPast   = !e.is_complete && scheduled && scheduled <  today;
                const isToday  = !e.is_complete && scheduled && scheduled === today;
                const scheduledClass = isPast
                  ? "text-red-600 font-semibold whitespace-nowrap"
                  : isToday
                    ? "text-orange-600 font-semibold whitespace-nowrap"
                    : "text-slate-900 font-medium whitespace-nowrap";
                // done + next kept in scope in case future actions reference them, but not rendered
                void done; void next;
                return (
                  <TableRow key={e.id} data-testid={`followup-row-${e.id}`}>
                    <TableCell className="text-slate-500 text-sm w-14" data-testid={`followup-sno-${e.id}`}>{i + 1}</TableCell>
                    <TableCell className={scheduledClass}
                        data-testid={`followup-scheduled-${e.id}`}>
                      {scheduled ? fmtDate(scheduled) : <span className="text-slate-400">—</span>}
                    </TableCell>
                    <TableCell className="font-medium text-slate-900">
                      <button type="button" onClick={() => setHistoryName(e.patient_name)}
                              data-testid={`followup-name-${e.id}`}
                              className="hover:text-sky-700 hover:underline text-left">
                        {e.patient_name}
                      </button>
                      {e.source === "lab_received" && (
                        <div className="mt-0.5">
                          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-violet-700 bg-violet-50 border border-violet-100 rounded px-1.5 py-0.5"
                                data-testid={`followup-source-lab-${e.id}`}
                                title="Auto-added when lab work was marked received">
                            <FlaskConical size={9} /> from Lab
                          </span>
                        </div>
                      )}
                    </TableCell>
                    <TableCell><StatusBadge status={e.status} /></TableCell>
                    <TableCell className="text-slate-600 max-w-xs truncate">{e.notes || "—"}</TableCell>
                    <TableCell className="text-right space-x-2 whitespace-nowrap">
                      <Button size="sm" variant="outline"
                              onClick={() => startEdit(e)}
                              data-testid={`followup-edit-${e.id}`}>
                        Edit
                      </Button>
                      {!e.is_complete && (
                        <Button size="sm" variant="outline"
                                onClick={() => startClose(e)}
                                data-testid={`followup-close-${e.id}`}>
                          <XCircle size={14} className="mr-1" /> Close
                        </Button>
                      )}
                      <Link to={`/entries/${e.id}`}>
                        <Button size="sm"
                                variant={e.is_complete ? "outline" : "default"}
                                className={e.is_complete ? "" : "bg-sky-500 hover:bg-sky-600 text-white"}
                                data-testid={`open-followup-${e.id}`}>
                          {e.is_complete ? "View" : "Open"}
                        </Button>
                      </Link>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      <EntryDialog
        open={editOpen}
        onOpenChange={(v) => { setEditOpen(v); if (!v) setEditEntry(null); }}
        entry={editEntry}
        onSaved={load}
        showCallLog={true}
      />
      <Dialog open={newOpen} onOpenChange={(v) => { if (!savingNew) { setNewOpen(v); if (!v) setNewName(""); } }}>
        <DialogContent data-testid="new-followup-dialog">
          <DialogHeader>
            <DialogTitle>New follow-up</DialogTitle>
            <DialogDescription>
              Add a patient to today's Pending list. You can fill in status, notes and call log later from the row.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNewFollowup} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new-followup-name">Patient name</Label>
              <Input
                id="new-followup-name"
                autoFocus
                required
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder="e.g. Anita Sharma"
                data-testid="new-followup-name-input"
              />
              <p className="text-xs text-slate-500">
                Added under Pending with today's date. Won't appear on the Daily Log.
              </p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" disabled={savingNew}
                      onClick={() => { setNewOpen(false); setNewName(""); }}
                      data-testid="new-followup-cancel">
                Cancel
              </Button>
              <Button type="submit" disabled={savingNew || !newName.trim()}
                      className="bg-sky-500 hover:bg-sky-600 text-white"
                      data-testid="new-followup-save">
                {savingNew ? "Adding…" : "Add to Pending"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <CloseFollowupDialog
        open={closeOpen}
        onOpenChange={(v) => { setCloseOpen(v); if (!v) setCloseEntry(null); }}
        entry={closeEntry}
        onClosed={load}
      />
      <CloseReasonsSettingsDialog
        open={reasonsOpen}
        onOpenChange={setReasonsOpen}
      />
      <PatientHistoryDialog
        open={!!historyName}
        onOpenChange={(v) => !v && setHistoryName(null)}
        patientName={historyName}
      />
    </div>
  );
}
